/** Borradores de producto y variante para formularios de catálogo (sin I/O). */
import type { Product } from './productTypes';
import type { ProductIvaRate } from './catalogProductDefaults';

export type ProductVariantDraft = {
  key: string;
  id?: string;
  nameSuffix: string;
  sku: string;
  barcode: string;
  price: string;
  /** Atributos dinámicos: { talle: "M", color: "Rojo" } o { fragancia: "Lavanda", tamano: "Chico" } */
  attributes: Record<string, string>;
  /** Cantidad en sucursal actual (solo ABM con variantes). */
  stockQty: string;
  /** URL pública de la imagen de la variante (null = usa la del producto). */
  imageUrl?: string | null;
};

export const emptyVariantDraft = (): ProductVariantDraft => ({
  key: `variant-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  nameSuffix: '',
  sku: '',
  barcode: '',
  price: '',
  attributes: {},
  stockQty: '',
  imageUrl: null,
});

export type ProductDraft = {
  name: string;
  /** Código de barras (EAN/UPC) si existe y es distinto del SKU. */
  barcode: string;
  sku: string;
  price: string;
  categoryId: Product['categoryId'];
  itemType: 'physical' | 'service';
  brandId: string;
  internalNotes: string;
  preferredSupplierId: string;
  supplierItemCode: string;
  ivaRate: ProductIvaRate;
  unitOfMeasure: Product['unitOfMeasure'];
  sellByWeight: boolean;
  /** Minutos; string vacío = sin duración. */
  durationMinutes: string;
  /** Buffer antes del turno (minutos); vacío = 0. */
  bufferBeforeMinutes: string;
  /** Buffer después del turno (minutos); vacío = 0. */
  bufferAfterMinutes: string;
  imageUrl: string;
  videoUrl: string;
  publicDescription: string;
  /** Cantidad mínima de venta online; vacío = sin mínimo. */
  minOrderQty: string;
  /** Cantidad máxima de venta online; vacío = sin máximo. */
  maxOrderQty: string;
  isPublic: boolean;
  /** 86 / pause for menu (portal). Default true. */
  menuAvailable: boolean;
  /** Si false, no aparece en la carta QR (/m). Default true. */
  visibleOnMenuCarta: boolean;
  activeIngredient: string;
  usageTags: string;
  /** Alérgenos que contiene (`products.allergens`). */
  allergens: string[];
  /** direct = slots online; quote_required = pedir cotización primero */
  bookingMode: 'direct' | 'quote_required';
  evaluationProductId: string;
  bookingPaymentMode: 'inherit' | 'none' | 'deposit' | 'full';
  bookingDepositAmount: string;
  bookingDepositPercent: string;
  /** Destino de producción: kitchen, bar, o none (no genera ticket). Default: kitchen. */
  kitchenDestination: 'kitchen' | 'bar' | 'none';
  /** Minutos objetivo de preparación; vacío = fallback de sucursal. */
  prepMinutes: string;
};

/**
 * Nombre inicial para el borrador de un duplicado.
 * "X" → "X (copia)"; si ya era copia, incrementa: "X (copia 2)", "X (copia 3)"…
 */
export function duplicateProductDraftName(name: string): string {
  const trimmed = name.trim();
  const match = /^(.*)\s*\(copia(?:\s+(\d+))?\)\s*$/i.exec(trimmed);
  if (!match) {
    const base = trimmed || 'Ítem';
    return `${base} (copia)`;
  }
  const base = match[1].trim() || 'Ítem';
  const next = match[2] ? Number.parseInt(match[2], 10) + 1 : 2;
  return `${base} (copia ${next})`;
}

export const emptyDraft: ProductDraft = {
  name: '',
  barcode: '',
  sku: '',
  price: '',
  categoryId: '',
  itemType: 'physical',
  brandId: '',
  internalNotes: '',
  preferredSupplierId: '',
  supplierItemCode: '',
  ivaRate: '21',
  unitOfMeasure: 'unit',
  sellByWeight: false,
  durationMinutes: '',
  bufferBeforeMinutes: '',
  bufferAfterMinutes: '',
  imageUrl: '',
  videoUrl: '',
  publicDescription: '',
  minOrderQty: '',
  maxOrderQty: '',
  isPublic: true,
  menuAvailable: true,
  visibleOnMenuCarta: true,
  activeIngredient: '',
  usageTags: '',
  allergens: [],
  bookingMode: 'direct',
  evaluationProductId: '',
  bookingPaymentMode: 'inherit',
  bookingDepositAmount: '',
  bookingDepositPercent: '',
  kitchenDestination: 'kitchen',
  prepMinutes: '',
};
